import { z } from 'zod';
import type {
  ITopFiveApiV2EndpointsTopFiveImportImportTopFiveResponse,
  ITopFiveApiV2EndpointsTopFiveImportImportTopFiveEndpointBody,
} from '@/api/models';

/**
 * TypeScript types and Zod schemas for CSV import feature.
 * Using generated types from OpenAPI spec for request/response.
 */

// ============================================================================
// Request Types - Using Generated API Types
// ============================================================================

/**
 * Import request body using generated API types
 */
export type ImportTopFiveRequestBody = ITopFiveApiV2EndpointsTopFiveImportImportTopFiveEndpointBody;

/**
 * Form data validation schema (local form state before API submission)
 */
export const ImportTopFiveRequestSchema = z.object({
  file: z.instanceof(File, { message: 'CSV file is required' }),
  periodId: z.string().min(1, { message: 'Please select a period' }),
});

export type ImportTopFiveRequest = z.infer<typeof ImportTopFiveRequestSchema>;

/**
 * Internal form data structure (used by the form before submission).
 * The period selection will be converted to startDate/endDate for the API.
 */
export interface ImportFormData {
  file: File;
  periodId: string; // JSON stringified PeriodOption for easy storage in select value
}

// ============================================================================
// Response Types - Using Generated API Types
// ============================================================================

/**
 * Import response using generated API types from OpenAPI spec
 */
export type ImportTopFiveResponse = Required<ITopFiveApiV2EndpointsTopFiveImportImportTopFiveResponse> & {
  rowErrors: Record<number, string[]>; // Override to use number keys instead of string keys
};

// ============================================================================
// UI State Types
// ============================================================================

export type ImportState = 'idle' | 'uploading' | 'processing' | 'success' | 'error';

export interface ImportProgress {
  loaded: number;
  total: number;
  percentage: number;
}

// ============================================================================
// Validation Helpers
// ============================================================================

export const validateCsvFile = (file: File): string | null => {
  // Check file type
  if (!file.name.endsWith('.csv')) {
    return 'Only CSV files are allowed (.csv extension)';
  }

  // Check file size (5MB limit)
  const maxSizeBytes = 5 * 1024 * 1024;
  if (file.size > maxSizeBytes) {
    return 'File size must not exceed 5MB';
  }

  // Check if file is empty
  if (file.size === 0) {
    return 'File cannot be empty';
  }

  return null;
};

export const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
};
